<template>
  <div
    v-clickoutside="clickoutside"
    v-resize="handleResize"
    class="s-select"
    :class="{'is-transparent': transparent, [`is-${size}`]: true}"
    :data-qa="dataQa"
    @click.stop="toggleMenu"
    @mouseleave="inputHovering = false"
    @mouseover="inputHovering = true">
    <div
      v-if="multiple"
      ref="tags"
      class="s-select__tags"
      :style="{ 'max-width': inputWidth - 32 + 'px', width: '100%' }">
      <span v-if="selected.length">
        <span
          class="s-select__text"
          :data-qa="`btn-${dataQa}-text`">
          <template v-if="selected.length > 1">{{ selected.length }} {{ tagLabel }}</template>
          <template v-else>{{ selected[0].currentLabel }}</template>
        </span>
      </span>
      <input
        v-if="filterable && !selectDisabled"
        ref="input"
        v-model="query"
        :autocomplete="autocomplete"
        class="s-select__input"
        :data-qa="`btn-${dataQa}-query`"
        :disabled="selectDisabled"
        :style="{ 'flex-grow': '1', width: inputLength / (inputWidth - 32) + '%', 'max-width': inputWidth - 42 + 'px' }"
        type="text"
        @blur="handleBlur"
        @compositionend="handleComposition"
        @compositionstart="handleComposition"
        @compositionupdate="handleComposition"
        @focus="handleFocus"
        @input="debouncedQueryChange"
        @keydown="resetInputState"
        @keydown.delete="deletePrevTag"
        @keydown.down.prevent="navigateOptions('next')"
        @keydown.enter.prevent="selectOption"
        @keydown.esc.stop.prevent="visible = false"
        @keydown.tab="visible = false"
        @keydown.up.prevent="navigateOptions('prev')"
        @keyup="managePlaceholder"
        @paste="debouncedOnInputChange">
    </div>
    <sInput
      :id="id"
      ref="reference"
      :autocomplete="autocomplete"
      :class="{ 'is-focus': visible }"
      :data-qa="`btn-${dataQa}-label`"
      :disabled="selectDisabled"
      :name="name"
      :placeholder="currentPlaceholder"
      :readonly="inputReadonly"
      :size="size"
      :tabindex="(multiple && filterable) ? '-1' : null"
      :transparent="transparent"
      type="text"
      :validate-event="false"
      :value.sync="selectedLabel"
      @blur="handleBlur"
      @focus="handleFocus"
      @keydown.native.down.stop.prevent="navigateOptions('next')"
      @keydown.native.enter.prevent="selectOption"
      @keydown.native.esc.stop.prevent="visible = false"
      @keydown.native.tab="visible = false"
      @keydown.native.up.stop.prevent="navigateOptions('prev')"
      @keyup.native="debouncedOnInputChange"
      @paste.native="debouncedOnInputChange">
      <template
        v-if="$slots.prefix"
        #prefix>
        <!-- @slot контент префикса -->
        <slot name="prefix"/>
      </template>
      <template #suffix>
        <div :style="{'cursor: pointer;': !selectDisabled}">
          <sIcon
            v-show="!showClose"
            class="s-select__caret s-input__icon"
            :data-qa="`btn-${dataQa}-arrow`"
            line-height="40px"
            :rotate="visible ? '-180' : '0'"
            size="10"
            type="arrow"/>
          <sButton
            v-if="showClose"
            class="s-input__clear"
            :data-qa="`btn-${dataQa}-clear`"
            icon="cross"
            small
            type="text"
            @click.stop="deleteSelected"/>
        </div>
      </template>
    </sInput>
    <transition
      name="s-zoom-in-top"
      @after-leave="doDestroy"
      @before-enter="handleMenuEnter">
      <sSelectDropdown
        v-show="visible && emptyText !== false"
        ref="popper"
        :append-to-body="popperAppendToBody"
        :is-active="visible">
        <sScrollbar
          v-show="filteredOptionsCount && options.length && !loading"
          ref="scrollbar"
          :class="{ 'is-empty': !allowCreate && query && filteredOptionsCount === 0 }"
          :data-qa="`${dataQa}-items`"
          tag="ul"
          view-class="s-select-dropdown__list"
          wrap-class="s-select-dropdown__wrap">
          <sOption
            v-if="showNewOption"
            created
            :data-qa="`${dataQa}-item-new`"
            :value="query"/>
          <!-- @slot Список дополнительных компонентов -->
          <slot/>
        </sScrollbar>
        <template v-if="emptyText && (!allowCreate || loading || (allowCreate && options.length === 0 ))">
          <!-- @slot когда нет данных -->
          <slot
            v-if="$slots.empty"
            name="empty"/>
          <p
            v-else
            class="s-select-dropdown__empty"
            :data-qa="`${dataQa}-item-dropdown__empty`">
            {{ emptyText }}
          </p>
        </template>
      </sSelectDropdown>
    </transition>
  </div>
</template>

<script>
import Form from '../../mixins/form.js'
import Emitter from '../../mixins/emitter'
import sInput from '../input'
import sSelectDropdown from './select-dropdown.vue'
import sOption from './option.vue'
import sScrollbar from '../scrollbar'

import Clickoutside from '../../utils/clickoutside'
import scrollIntoView from '../../utils/scroll-into-view'
import { getValueByPath, valueEquals, isIE, isEdge } from '../../utils/util'
import NavigationMixin from './navigation-mixin'

/**
 * Стандартный Select
 * @displayName Select
 */

export default {
  mixins: [Emitter, Form, NavigationMixin],
  name: 'sSelect',
  componentName: 'sSelect',
  provide() {
    return {
      'select': this
    }
  },
  computed: {
    inputReadonly() {
      return !this.filterable || this.multiple || (!isIE() && !isEdge() && !this.visible)
    },
    showClose() {
      const checkValue = (value) => {
        return value !== undefined && value !== null && value !== ''
      }
      let hasValue = checkValue(this.value)

      if (this.multiple && Array.isArray(this.value)) {
        hasValue = this.value.length > 0
      } else if (!this.multiple && this.value && typeof this.value === 'object') {
        hasValue = checkValue(this.value[this.valueKey])
      }


      let criteria = this.clearable &&
          !this.selectDisabled &&
          this.inputHovering &&
          hasValue
      return criteria
    },
    debounce() {
      return this.remote ? 300 : 0
    },
    emptyText() {
      if (this.loading) {
        return this.loadingText
      } else {
        if (this.remote && this.query === '' && this.options.length === 0) { return this.noDataText }
        if (this.filterable && this.query && this.options.length > 0 && this.filteredOptionsCount === 0) {
          return this.noMatchText
        }
        if (this.options.length === 0) {
          return this.noDataText
        }
      }
      return null
    },
    showNewOption() {
      let hasExistingOption = this.options.filter(option => !option.created)
        .some(option => option.currentLabel === this.query)
      return this.filterable && this.allowCreate && this.query !== '' && !hasExistingOption
    },
    selectDisabled() {
      return this.disabled || (this.sForm || {}).disabled
    },
  },
  components: {
    sInput,
    sOption,
    sScrollbar,
    sSelectDropdown,
  },
  directives: { Clickoutside },
  props: {
    /**
     * связывание значения
     */
    value: {
      type: [String, Number, Array, Object, Boolean],
    },
    /**
     * Без рамок, фона и меньшей высоты
     */
    transparent: {
      type: Boolean,
      default: false
    },
    /**
     * атрибут name выбранного ввода
     * @ignore
     */
    name: String,
    /**
     * @ignore
     */
    id: String,
    /**
     * атрибут autocomplete
     */
    autocomplete: {
      type: String,
      default: 'off'
    },
    /**
     * для нефильтруемого выбора, этот параметр определяет, всплывает ли меню параметров, когда ввод находится в фокусе
     */
    automaticDropdown: Boolean,
    autoOpen: Boolean,
    /**
     * отключен ли Select
     */
    disabled: Boolean,
    /**
     * можно ли очистить выбор
     */
    clearable: Boolean,
    /**
     * можно ли фильтровать
     */
    filterable: Boolean,
    /**
     * разрешено ли создание новых элементов. Чтобы использовать это, filterable должна быть true.
     */
    allowCreate: Boolean,
    /**
     * загружает ли Select данные с сервера
     */
    loading: Boolean,
    /**
     * отображаемый текст при загрузке данных с сервера
     */
    loadingText: {
      type: String,
      default: 'Завантаження'
    },
    /**
     * имя настраиваемого класса для раскрывающегося списка
     */
    popperClass: {
      type: String,
      default: ''
    },
    /**
     * загружаются ли параметры с сервера
     */
    remote: Boolean,
    /**
     * отображаемый текст, когда нет данных, соответствующих запросу фильтрации, вы также можете использовать слот empty
     */
    noMatchText: {
      type: String,
      default: 'Співпадінь не знайдено'
    },
    /**
    * отображаемый текст, когда нет параметров, вы также можете использовать слот empty
    */
    noDataText: {
      type: String,
      default: 'Немає даних'
    },
    /**
    * настраиваемый метод удаленного поиска
    */
    remoteMethod: Function,
    /**
     * настраиваемый метод фильтрации
     */
    filterMethod: Function,
    /**
     * множественный выбор
     */
    multiple: Boolean,
    /**
     * максимальное количество опций, которые пользователь может выбрать. Без ограничений при значении 0
     */
    multipleLimit: {
      type: Number,
      default: 0
    },
    /**
     * placeholder
     */
    placeholder: {
      type: String,
      default: 'Обрати'
    },
    /**
     * выберать первый вариант соответствия при нажатии клавиши ввода. Используйте с `filterable` или `remote`
     */
    defaultFirstOption: Boolean,
    /**
     * если `multiple` и `filter` true, следует ли зарезервировать текущее ключевое слово после выбора параметра
     */
    reserveKeyword: Boolean,
    /**
     * уникальное имя ключа идентификации для значения, требуется, когда значение является объектом
     */
    valueKey: {
      type: String,
      default: 'value'
    },
    /**
     * Тег в свернутом виде.
     */
    tagLabel: {
      type: String,
      default: 'обрано'
    },
    /**
     * добавлять ли всплывающее меню к body. Если позиционирование всплывающего меню неправильное, вы можете попытаться установить для false.
     */
    popperAppendToBody: {
      type: Boolean,
      default: true
    },
    dataQa: {
      type: String,
      default: 'select'
    },
    size: {
      type: String,
      default: 'normal',
      validator(value) {
        return ['small', 'normal'].indexOf(value) !== -1
      }
    },
  },
  data() {
    return {
      options: [],
      cachedOptions: [],
      createdLabel: null,
      createdSelected: false,
      selected: this.multiple ? [] : {},
      inputLength: 20,
      inputWidth: 0,
      initialInputHeight: 0,
      cachedPlaceHolder: '',
      optionsCount: 0,
      filteredOptionsCount: 0,
      visible: false,
      softFocus: false,
      selectedLabel: '',
      hoverIndex: -1,
      query: '',
      previousQuery: null,
      inputHovering: false,
      currentPlaceholder: '',
      menuVisibleOnFocus: false,
      isOnComposition: false,
      isSilentBlur: false
    }
  },
  watch: {
    selectDisabled() {
      this.$nextTick(() => {
        let height = this.$refs?.reference?.$el?.querySelector('input')?.getBoundingClientRect()?.height
        this.initialInputHeight = !height || height < 40 ? 40 : height
        this.resetInputHeight()
      })
    },
    value(val, oldVal) {
      if (this.multiple) {
        this.resetInputHeight()
        if ((val && val.length > 0) || (this.$refs.input && this.query !== '')) {
          this.currentPlaceholder = ''
        } else {
          this.currentPlaceholder = this.placeholder || ''
        }
        if (this.filterable && !this.reserveKeyword) {
          this.query = ''
          this.handleQueryChange(this.query)
        }
      }
      this.setSelected()
      if (this.filterable && !this.multiple) {
        this.inputLength = 20
      }
      if (!valueEquals(val, oldVal)) {
        this.dispatch('sFormItem', 's.form.change', val)
      }
    },
    visible(val) {
      if (!val) {
        this.broadcast('sSelectDropdown', 'destroyPopper')
        if (this.$refs.input) {
          this.$refs.input.blur()
        }
        //this.query = ''
        //this.previousQuery = null
        this.selectedLabel = ''
        this.inputLength = 20
        this.menuVisibleOnFocus = false
        this.resetHoverIndex()
        this.$nextTick(() => {
          if (this.$refs.input &&
              this.$refs.input.value === '' &&
              this.selected.length === 0) {
            this.currentPlaceholder = this.cachedPlaceHolder
          }
        })
        if (!this.multiple) {
          if (this.selected) {
            if (this.filterable && this.allowCreate &&
                this.createdSelected && this.createdLabel) {
              this.selectedLabel = this.createdLabel
            } else {
              this.selectedLabel = this.selected.currentLabel
            }
            if (this.filterable) {
              this.query = this.selectedLabel
            }
          }
          if (this.filterable) {
            this.currentPlaceholder = this.cachedPlaceHolder
          }
        }
      } else {
        this.broadcast('sSelectDropdown', 'updatePopper')
        if (this.filterable) {
          if (!this.remote) { this.query = this.selectedLabel }
          // this.query = this.remote ? '' : this.selectedLabel
          this.handleQueryChange(this.query)
          if (this.multiple) {
            this.$refs.input.focus()
          } else {
            if (!this.remote) {
              this.broadcast('sOption', 'queryChange', '')
              this.broadcast('sOptionGroup', 'queryChange')
            }
            if (this.selectedLabel) {
              this.currentPlaceholder = this.selectedLabel
              this.selectedLabel = ''
            }
          }
        }
      }

      /**
      * срабатывает, когда раскрывающийся список появляется / исчезает
      * @event visible-change
      */
      this.$emit('visible-change', val)
      if (!val) {
        this.$emit('close')
      }
    },
    options() {
      if (this.$isServer) { return }
      this.$nextTick(() => {
        this.broadcast('sSelectDropdown', 'updatePopper')
      })
      if (this.multiple) {
        this.resetInputHeight()
      }
      let inputs = this.$el.querySelectorAll('input')
      if ([].indexOf.call(inputs, document.activeElement) === -1) {
        this.setSelected()
      }
      if (this.defaultFirstOption && (this.filterable || this.remote) && this.filteredOptionsCount) {
        this.checkDefaultFirstOption()
      }
    }
  },
  methods: {
    clickoutside(e) {
      if (this.popperElm && this.popperElm.contains(e.target)) { return }
      this.$emit('clickoutside')
      this.handleClose()
    },
    handleComposition(event) {
      const text = event.target.value
      if (event.type === 'compositionend') {
        this.isOnComposition = false
        this.$nextTick(() => this.handleQueryChange(text))
      } else {
        this.isOnComposition = true
      }
    },
    handleQueryChange(val) {
      if (this.previousQuery === val || this.isOnComposition) { return }
      if (
        this.previousQuery === null &&
          (typeof this.filterMethod === 'function' || typeof this.remoteMethod === 'function')
      ) {
        this.previousQuery = val
        return
      }
      this.previousQuery = val
      this.$nextTick(() => {
        if (this.visible) { this.broadcast('sSelectDropdown', 'updatePopper') }
      })
      this.hoverIndex = -1
      if (this.multiple && this.filterable) {
        this.$nextTick(() => {
          const length = this.$refs.input.value.length * 15 + 20
          this.inputLength = Math.min(50, length)
          this.managePlaceholder()
          this.resetInputHeight()
        })
      }
      if (this.remote && typeof this.remoteMethod === 'function') {
        this.hoverIndex = -1
        this.remoteMethod(val)
      } else if (typeof this.filterMethod === 'function') {
        this.filterMethod(val)
        this.broadcast('sOptionGroup', 'queryChange')
      } else {
        this.filteredOptionsCount = this.optionsCount
        this.broadcast('sOption', 'queryChange', val)
        this.broadcast('sOptionGroup', 'queryChange')
      }
      if (this.defaultFirstOption && (this.filterable || this.remote) && this.filteredOptionsCount) {
        this.checkDefaultFirstOption()
      }
    },
    scrollToOption(option) {
      const target = Array.isArray(option) && option[0] ? option[0].$el : option.$el
      if (this.$refs.popper && target) {
        const menu = this.$refs.popper.$el.querySelector('.s-select-dropdown__wrap')
        scrollIntoView(menu, target)
      }
      this.$refs.scrollbar && this.$refs.scrollbar.handleScroll()
    },
    handleMenuEnter() {
      this.$nextTick(() => this.scrollToOption(this.selected))
    },
    emitChange(val) {
      if (!valueEquals(this.value, val)) {
        if (val && Array.isArray(val)) {
          const isOldValue = this.value?.length
          const isNewValue = val?.length
          if (isOldValue !== isNewValue || JSON.stringify(this.value) !== JSON.stringify(val)) {
            /**
            * срабатывает при добавлении или удалении эллементов в массив
            * @event changeArray
            */
            this.$emit('changeArray')
          }

          if (isOldValue !== isNewValue) {

            let deltaValue = []
            let eventName = ''
            if (!isOldValue) {
              deltaValue = val
              eventName = 'addedItem'
            } else if (!isNewValue) {
              deltaValue = this.value
              eventName = 'removedItem'
            } else {
              const deltaObject = (firstObject, secondObject) => {
                return secondObject.filter(id1 => !firstObject.some(id2 => id2 === id1))
              }
              eventName = isNewValue > isOldValue ? 'addedItem' : 'removedItem'
              deltaValue = isNewValue > isOldValue ? deltaObject(this.value, val) : deltaObject(val, this.value)
            }
            /**
            * срабатывает при добавлении или удалении эллементов в массив
            * @event addedItem/removedItem
            * @property {array} Удаленные или добавленные данные
            */
            this.$emit(eventName, deltaValue)
          }
        }
        /**
        * срабатывает при изменении выбранного значения
        * @event update:value
        */
        this.$emit('update:value',  val)
      }
    },
    getOption(value) {
      let option
      const isObject = Object.prototype.toString.call(value).toLowerCase() === '[object object]'
      const isNull = Object.prototype.toString.call(value).toLowerCase() === '[object null]'
      const isUndefined = Object.prototype.toString.call(value).toLowerCase() === '[object undefined]'
      for (let i = this.cachedOptions.length - 1; i >= 0; i--) {
        const cachedOption = this.cachedOptions[i]
        const isEqual = isObject
          ? getValueByPath(cachedOption.value, this.valueKey) === getValueByPath(value, this.valueKey)
          : cachedOption.value === value
        if (isEqual) {
          option = cachedOption
          break
        }
      }
      if (option) { return option }
      const label = (!isObject && !isNull && !isUndefined)
        ? value : ''
      let newOption = {
        value: value,
        currentLabel: label
      }
      if (this.multiple) {
        newOption.hitState = false
      }
      return newOption
    },
    setSelected() {
      if (!this.multiple) {
        let option = this.getOption(this.value)
        if (option.created) {
          this.createdLabel = option.currentLabel
          this.createdSelected = true
        } else {
          this.createdSelected = false
        }
        this.selectedLabel = option.currentLabel
        this.selected = option
        if (this.filterable) {
          this.query = this.selectedLabel
        }
        return
      }
      let result = []
      if (Array.isArray(this.value)) {
        this.value.forEach(value => {
          result.push(this.getOption(value))
        })
      }
      this.selected = result
      this.$nextTick(() => {
        this.resetInputHeight()
      })
    },
    handleFocus(event) {
      if (!this.softFocus) {
        if (this.automaticDropdown || this.filterable) {
          this.visible = true
          if (this.filterable) {
            this.menuVisibleOnFocus = true
          }
        }

        /**
        * срабатывает, когда ввод фокусируется
        * @event change
        */
        this.$emit('focus', event)
      } else {
        this.softFocus = false
      }
    },
    blur() {
      this.visible = false
      this.$refs.reference.blur()
    },
    handleBlur(event) {
      setTimeout(() => {
        if (this.isSilentBlur) {
          this.isSilentBlur = false
        } else {
          /**
          * срабатывает, когда ввод blur
          * @event change
          */
          this.$emit('blur', event)
        }
      }, 50)
      this.softFocus = false
    },
    doDestroy() {
      this.$refs.popper && this.$refs.popper.doDestroy()
    },
    handleClose() {
      this.visible = false
      this.$emit('close')
    },
    toggleLastOptionHitState(hit) {
      if (!Array.isArray(this.selected)) { return }
      const option = this.selected[this.selected.length - 1]
      if (!option) { return }
      if (hit === true || hit === false) {
        option.hitState = hit
        return hit
      }
      option.hitState = !option.hitState
      return option.hitState
    },
    deletePrevTag(e) {
      if (e.target.value.length <= 0 && !this.toggleLastOptionHitState()) {
        const value = this.value.slice()
        value.pop()
        this.emitChange(value)
      }
    },
    managePlaceholder() {
      if (this.currentPlaceholder !== '') {
        this.currentPlaceholder = this.$refs.input.value ? '' : this.cachedPlaceHolder
      }
    },
    resetInputState(e) {
      if (e.keyCode !== 8) { this.toggleLastOptionHitState(false) }
      this.inputLength = this.$refs.input.value.length * 15 + 20
      this.resetInputHeight()
    },
    resetInputHeight() {
      if (!this.filterable) { return }
      this.$nextTick(() => {
        if (!this.$refs.reference) { return }
        let inputChildNodes = this.$refs.reference.$el.childNodes
        let input = [].filter.call(inputChildNodes, item => item.tagName === 'INPUT')[0]
        const tags = this.$refs.tags
        const sizeInMap = this.initialInputHeight || 40
        input.style.height = this.selected.length === 0
          ? sizeInMap + 'px'
          : Math.max(
            tags ? (tags.clientHeight + (tags.clientHeight > sizeInMap ? 6 : 0)) : 0,
            sizeInMap
          ) + 'px'
        if (this.visible && this.emptyText !== false) {
          this.broadcast('sSelectDropdown', 'updatePopper')
        }
      })
    },
    resetHoverIndex() {
      setTimeout(() => {
        if (!this.multiple) {
          this.hoverIndex = this.options.indexOf(this.selected)
        } else {
          if (this.selected.length > 0) {
            this.hoverIndex = Math.min.apply(null, this.selected.map(item => this.options.indexOf(item)))
          } else {
            this.hoverIndex = -1
          }
        }
      }, 300)
    },
    handleOptionSelect(option, byClick) {
      if (this.multiple) {
        const value = (this.value || []).slice()
        const optionIndex = this.getValueIndex(value, option.value)
        if (optionIndex > -1) {
          value.splice(optionIndex, 1)
        } else if (this.multipleLimit <= 0 || value.length < this.multipleLimit) {
          value.push(option.value)
        }
        this.emitChange(value)
        if (option.created) {
          this.query = ''
          this.handleQueryChange('')
          this.inputLength = 20
        }
        if (this.filterable) { this.$refs.input.focus() }
      } else {
        this.emitChange(option.value)
        this.visible = false
      }
      this.isSilentBlur = byClick
      this.setSoftFocus()
      if (this.visible) { return }
      this.$nextTick(() => {
        this.scrollToOption(option)
      })
    },
    setSoftFocus() {
      this.softFocus = true
      const input = this.$refs.input || this.$refs.reference
      if (input) {
        input.focus()
      }
    },
    getValueIndex(arr = [], value) {
      const isObject = Object.prototype.toString.call(value).toLowerCase() === '[object object]'
      if (!isObject) {
        return arr.indexOf(value)
      } else {
        const valueKey = this.valueKey
        let index = -1
        arr.some((item, i) => {
          if (getValueByPath(item, valueKey) === getValueByPath(value, valueKey)) {
            index = i
            return true
          }
          return false
        })
        return index
      }
    },
    toggleMenu() {
      this.handleResize()
      if (!this.selectDisabled) {
        if (this.menuVisibleOnFocus) {
          this.menuVisibleOnFocus = false
        } else {
          this.$root.$emit('closeAllSelect')
          this.visible = !this.visible
        }
        if (this.visible) {
          (this.$refs.input || this.$refs.reference).focus()
        }
      }
    },
    selectOption() {
      if (!this.visible) {
        this.toggleMenu()
      } else {
        if (this.options[this.hoverIndex]) {
          this.handleOptionSelect(this.options[this.hoverIndex])
        }
      }
    },
    deleteSelected() {
      event.stopPropagation()
      const value = this.multiple ? [] : null
      this.emitChange(value)
      this.visible = false
      /**
      * срабатывает при очистке всех значений
      * @event clear
      */
      this.$emit('clear')
    },
    onInputChange() {
      if (this.filterable && this.query !== this.selectedLabel) {
        this.query = this.selectedLabel
        this.handleQueryChange(this.query)
      }
    },
    onOptionDestroy(index) {
      if (index > -1) {
        this.optionsCount--
        this.filteredOptionsCount--
        this.options.splice(index, 1)
      }
    },
    resetInputWidth() {
      if (!this.$refs.reference) { return }
      this.inputWidth = this.$refs.reference.$el.getBoundingClientRect().width
    },
    handleResize() {
      this.resetInputWidth()
      if (this.multiple) { this.resetInputHeight() }
    },
    checkDefaultFirstOption() {
      this.hoverIndex = -1
      // выделить созданный вариант
      let hasCreated = false
      for (let i = this.options.length - 1; i >= 0; i--) {
        if (this.options[i].created) {
          hasCreated = true
          this.hoverIndex = i
          break
        }
      }
      if (hasCreated) { return }
      for (let i = 0; i !== this.options.length; ++i) {
        const option = this.options[i]
        if (this.query) {
          // выделить первые параметры, прошедшие фильтр
          if (!option.disabled && !option.groupDisabled && option.visible) {
            this.hoverIndex = i
            break
          }
        } else {
          // выделить текущий выбранный вариант
          if (option.itemSelected) {
            this.hoverIndex = i
            break
          }
        }
      }
    },
    getValueKey(item) {
      if (Object.prototype.toString.call(item.value).toLowerCase() !== '[object object]') {
        return item.value
      } else {
        return getValueByPath(item.value, this.valueKey)
      }
    },
    debouncedQueryChange(e) {
      this.handleQueryChange(e.target.value)
    },
    debouncedOnInputChange() {
      return this.onInputChange()
    }
  },
  created() {
    this.cachedPlaceHolder = this.currentPlaceholder = this.placeholder
    this.$on('handleOptionClick', this.handleOptionSelect)
    this.$on('setSelected', this.setSelected)
    if (this.autoOpen) {
      this.visible = true
    }
  },
  mounted() {
    this.$root.$on('closeAllSelect', this.handleClose)
    if (this.multiple && Array.isArray(this.value) && this.value.length > 0) {
      this.currentPlaceholder = ''
    }
    const reference = this.$refs.reference
    if (reference && reference.$el) {
      const input = reference.$el.querySelector('input')
      this.initialInputHeight = input.getBoundingClientRect().height
    }
    if (this.remote && this.multiple) {
      this.resetInputHeight()
    }
    this.$nextTick(() => {
      if (reference && reference.$el) {
        this.inputWidth = reference.$el.getBoundingClientRect().width
      }
    })
    this.setSelected()
    this.$nextTick(() => {
      this.handleResize()
    })
  },
  beforeDestroy() {
    this.$root.$off('closeAllSelect', this.handleClose)
  },
}
</script>
